import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DeselectIcon from '@mui/icons-material/Deselect';
import type {
  SegmentRow,
  SegmentVehicleItem,
} from '../apiService/emailsGridApiService';
import type { VehicleListKind } from '../hooks/useSegmentInventories';
import CopyToClipboard from 'app/components/CopyToClipboard';

export interface PreferencesContentProps {
  /**
   * Toggle that signals the consumer modal opened or the active step became this view.
   * Used to reset internal search and snapshot state so the view is fresh each time.
   */
  isOpen: boolean;
  segmentRows: SegmentRow[];
  loading: boolean;
  error: string | null;
  headerRow: string[];
  expandedCell: { segmentName: string; kind: VehicleListKind } | null;
  vehiclesForExpandedCell: SegmentVehicleItem[];
  vehiclesLoading: boolean;
  vehiclesError: string | null;
  preferredVinsForExpandedCell: string[];
  missingVinsForExpandedCell: string[];
  onTogglePreferredVin: (
    segmentName: string,
    kind: VehicleListKind,
    vin: string,
    selected: boolean,
  ) => void;
  onToggleExpanded: (segmentName: string, kind: VehicleListKind) => void;
}

interface VehicleCardProps {
  vehicle: SegmentVehicleItem;
  checked: boolean;
  onToggle: () => void;
}

/** Card for a VIN that is in preferences but not in segment-inventory — selectable and selected by default. */
function MissingVinCard({
  vin,
  checked,
  onToggle,
}: {
  vin: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        minHeight: 56,
        position: 'relative',
        bgcolor: 'grey.100',
        borderColor: 'grey.300',
        borderStyle: 'dashed',
        cursor: 'default',
      }}
    >
      <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}>
        <Checkbox
          size="medium"
          checked={checked}
          onChange={onToggle}
          onClick={e => e.stopPropagation()}
          sx={{
            p: 0.2,
            transform: 'scale(1.07)',
            transformOrigin: 'top right',
            '& .MuiSvgIcon-root': { fontSize: 24 },
          }}
          aria-label={`Select missing VIN ${vin}`}
        />
      </Box>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Missing VIN
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              fontFamily: 'monospace',
              textDecoration: 'line-through',
              color: 'text.secondary',
            }}
          >
            {vin}
          </Typography>
          <CopyToClipboard text={vin} size="small" />
        </Box>
      </CardContent>
    </Card>
  );
}

function VehicleCard({ vehicle, checked, onToggle }: VehicleCardProps) {
  const validForNurturing =
    (vehicle as any).ValidForNurturing ?? (vehicle as any).validForNurturing;
  const hasValidity = validForNurturing !== undefined && validForNurturing !== null;
  const isValidForNurturing =
    validForNurturing === true ||
    validForNurturing === 'true' ||
    validForNurturing === 1 ||
    String(validForNurturing).toLowerCase() === 'true';

  const isCpoCertifiedRaw = (vehicle as any).IsCertified;
  const isCpoCertified =
    isCpoCertifiedRaw === true ||
    isCpoCertifiedRaw === 'true' ||
    isCpoCertifiedRaw === 1 ||
    String(isCpoCertifiedRaw).toLowerCase() === 'true';

  const isHtmlRaw = (vehicle as any).IsHtml;
  const isHtmlStatusRaw = (() => {
    if (typeof isHtmlRaw === 'string') return isHtmlRaw.toUpperCase();
    if (isHtmlRaw === true || isHtmlRaw === 1 || isHtmlRaw === '1') return 'ACTIVE';
    return 'NOT_AVAILABLE';
  })();
  const showHtmlBadge = isHtmlStatusRaw === 'ACTIVE' || isHtmlStatusRaw === 'EXPIRED';
  const htmlBadgeBg = isHtmlStatusRaw === 'ACTIVE' ? 'success.main' : 'grey.500';
  const daysInLotRaw =
    (vehicle as any).days_in_lot ?? (vehicle as any).DaysInLot ?? (vehicle as any).daysInLot;
  const daysInLot =
    daysInLotRaw !== undefined && daysInLotRaw !== null && !Number.isNaN(Number(daysInLotRaw))
      ? Number(daysInLotRaw)
      : null;

  const backgroundColor = hasValidity
    ? isValidForNurturing
      ? 'rgba(46, 125, 50, 0.08)'
      : 'rgba(211, 47, 47, 0.08)'
    : 'background.paper';

  const hoverBackgroundColor = hasValidity
    ? isValidForNurturing
      ? 'rgba(46, 125, 50, 0.12)'
      : 'rgba(211, 47, 47, 0.12)'
    : 'action.hover';

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        position: 'relative',
        cursor: 'default',
        bgcolor: backgroundColor,
        '&:hover': { bgcolor: hoverBackgroundColor },
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          <Checkbox
            size="medium"
            checked={checked}
            onChange={onToggle}
            onClick={e => e.stopPropagation()}
            sx={{
              p: 0.2,
              transform: 'scale(1.07)',
              transformOrigin: 'top right',
              '& .MuiSvgIcon-root': { fontSize: 24 },
            }}
            aria-label={`Select VIN ${vehicle.VIN}`}
          />
        </Box>
        {isCpoCertified && (
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              fontFamily: 'monospace',
              color: 'text.primary',
              lineHeight: 1.1,
              userSelect: 'none',
            }}
          >
            CPO
          </Typography>
        )}
      </Box>

      {showHtmlBadge && (
        <Tooltip
          arrow
          placement="top"
          title={
            isHtmlStatusRaw === 'ACTIVE'
              ? 'template is active'
              : 'template is expired'
          }
        >
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              zIndex: 1,
              bgcolor: htmlBadgeBg,
              color: 'common.white',
              width: 20,
              height: 20,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
            }}
          >
            {isHtmlStatusRaw === 'ACTIVE' ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'inherit' }} />
            ) : (
              <CancelOutlinedIcon sx={{ fontSize: 14, color: 'inherit' }} />
            )}
          </Box>
        </Tooltip>
      )}
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" display="block">
          VIN
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
            {vehicle.VIN}
          </Typography>
          <CopyToClipboard text={vehicle.VIN} size="small" />
        </Box>
        <Grid container spacing={0.75}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.1 }}>Year</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.74rem', lineHeight: 1.2 }}>{vehicle.Year}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.1 }}>Make</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.74rem', lineHeight: 1.2 }}>{vehicle.Make}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.1 }}>Model</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.74rem', lineHeight: 1.2 }}>{vehicle.Model}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.1 }}>Trim</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.74rem', lineHeight: 1.2 }}>{vehicle.Trim || '—'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.1 }}>Price</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.74rem', lineHeight: 1.2 }}>${Number(vehicle.Price ?? 0).toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.1 }}>MSRP</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.74rem', lineHeight: 1.2 }}>${Number(vehicle.MSRP ?? 0).toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.1 }}># days in lot</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.74rem', lineHeight: 1.2 }}>{daysInLot ?? '—'}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

/**
 * Reusable preferences body: segments table + expanded VIN cards.
 * Used by both `PreferencesModal` (EmailsGridPage) and `TestTemplateGenerationModal` (TestNurturingPage).
 */
export function PreferencesContent({
  isOpen,
  segmentRows,
  headerRow,
  loading,
  error,
  expandedCell,
  vehiclesForExpandedCell,
  vehiclesLoading,
  vehiclesError,
  preferredVinsForExpandedCell,
  missingVinsForExpandedCell,
  onTogglePreferredVin,
  onToggleExpanded,
}: PreferencesContentProps) {
  const [inventorySearch, setInventorySearch] = React.useState('');

  /** Snapshot of preferred VINs taken first time data arrives — sort order stable during manual toggles. */
  const [initialPreferredVins, setInitialPreferredVins] = React.useState<string[]>([]);
  const hasInitializedRef = React.useRef(false);

  React.useEffect(() => {
    hasInitializedRef.current = false;
    setInitialPreferredVins([]);
    setInventorySearch('');
  }, [isOpen, expandedCell?.segmentName, expandedCell?.kind]);

  React.useEffect(() => {
    if (!hasInitializedRef.current && preferredVinsForExpandedCell.length > 0) {
      hasInitializedRef.current = true;
      setInitialPreferredVins(preferredVinsForExpandedCell);
    }
  }, [preferredVinsForExpandedCell]);

  const filteredVehicles = React.useMemo(() => {
    const q = inventorySearch.trim().toLowerCase();
    const filtered = q
      ? vehiclesForExpandedCell.filter(v => {
          const vin = String(v.VIN ?? '').toLowerCase();
          const year = String(v.Year ?? '').toLowerCase();
          const make = String(v.Make ?? '').toLowerCase();
          const model = String(v.Model ?? '').toLowerCase();
          const trim = String(v.Trim ?? '').toLowerCase();
          const price = String(v.Price ?? '').toLowerCase();
          const daysInLot = String(
            (v as any).days_in_lot ?? (v as any).DaysInLot ?? (v as any).daysInLot ?? '',
          ).toLowerCase();
          const priceFormatted = (v.Price != null ? Number(v.Price).toLocaleString() : '').toLowerCase();
          return (
            vin.includes(q) ||
            year.includes(q) ||
            make.includes(q) ||
            model.includes(q) ||
            trim.includes(q) ||
            daysInLot.includes(q) ||
            price.includes(q) ||
            priceFormatted.includes(q)
          );
        })
      : vehiclesForExpandedCell;

    return [...filtered].sort((a, b) => {
      const aSelected = initialPreferredVins.includes(a.VIN);
      const bSelected = initialPreferredVins.includes(b.VIN);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [vehiclesForExpandedCell, inventorySearch, initialPreferredVins]);

  const filteredMissingVins = React.useMemo(() => {
    if (!inventorySearch.trim()) return missingVinsForExpandedCell;
    const q = inventorySearch.trim().toLowerCase();
    return missingVinsForExpandedCell.filter(vin =>
      String(vin).toLowerCase().includes(q),
    );
  }, [missingVinsForExpandedCell, inventorySearch]);

  const handleToggleVehicle = React.useCallback(
    (vin: string) => {
      if (!expandedCell) return;
      const currentlySelected = preferredVinsForExpandedCell.includes(vin);
      onTogglePreferredVin(expandedCell.segmentName, expandedCell.kind, vin, !currentlySelected);
    },
    [expandedCell, preferredVinsForExpandedCell, onTogglePreferredVin],
  );

  /**
   * Bulk-deselect every preferred VIN in the currently expanded segment+kind.
   * Relies on the upstream `togglePreferredVin` using functional setState so back-to-back calls
   * batched in the same React commit each see the latest prev state.
   */
  const handleClearSelections = React.useCallback(() => {
    if (!expandedCell || preferredVinsForExpandedCell.length === 0) return;
    const vinsToClear = [...preferredVinsForExpandedCell];
    vinsToClear.forEach((vin) => {
      onTogglePreferredVin(expandedCell.segmentName, expandedCell.kind, vin, false);
    });
  }, [expandedCell, preferredVinsForExpandedCell, onTogglePreferredVin]);

  const hasSelections = preferredVinsForExpandedCell.length > 0;

  const headers = headerRow?.length >= 3 ? headerRow : ['Segment', 'New Vehicles', 'Used Vehicles'];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, flexShrink: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((h, i) => (
                <TableCell key={i} sx={{ fontWeight: 600 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {segmentRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No segments
                </TableCell>
              </TableRow>
            ) : (
              segmentRows.map((row) => {
                const isNewActive =
                  expandedCell?.segmentName === row.segment_name && expandedCell?.kind === 'new';
                const isUsedActive =
                  expandedCell?.segmentName === row.segment_name && expandedCell?.kind === 'used';
                return (
                  <TableRow key={row.segment_name}>
                    <TableCell>{row.segment_name}</TableCell>
                    <TableCell
                      sx={{
                        ...(isNewActive && {
                          bgcolor: 'action.selected',
                          fontWeight: 600,
                          borderRadius: 0,
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                        }),
                      }}
                    >
                      <Typography
                        component="button"
                        variant="body2"
                        onClick={() => onToggleExpanded(row.segment_name, 'new')}
                        sx={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: isNewActive ? 'primary.main' : 'primary.main',
                          textDecoration: 'underline',
                          fontWeight: isNewActive ? 600 : 'inherit',
                          p: 0,
                          font: 'inherit',
                          '&:hover': { color: 'primary.dark' },
                        }}
                      >
                        {row.new?.count ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...(isUsedActive && {
                          bgcolor: 'action.selected',
                          fontWeight: 600,
                          borderRadius: 0,
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                        }),
                      }}
                    >
                      <Typography
                        component="button"
                        variant="body2"
                        onClick={() => onToggleExpanded(row.segment_name, 'used')}
                        sx={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: isUsedActive ? 'primary.main' : 'primary.main',
                          textDecoration: 'underline',
                          fontWeight: isUsedActive ? 600 : 'inherit',
                          p: 0,
                          font: 'inherit',
                          '&:hover': { color: 'primary.dark' },
                        }}
                      >
                        {row.used?.count ?? 0}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {expandedCell && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, flexShrink: 0 }}>
            Inventories — {expandedCell.segmentName} (
            {expandedCell.kind === 'new' ? 'New Vehicles' : 'Used Vehicles'})
          </Typography>
          {vehiclesError && (
            <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }}>
              {vehiclesError}
            </Alert>
          )}
          {vehiclesLoading && (
            <Box display="flex" justifyContent="center" py={3} sx={{ flexShrink: 0 }}>
              <CircularProgress />
            </Box>
          )}
          {!vehiclesLoading &&
            (vehiclesForExpandedCell.length > 0 || missingVinsForExpandedCell.length > 0) && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1.5,
                  flexShrink: 0,
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search by VIN, Year, Make, Model, Trim, Price, Days In Lot..."
                  value={inventorySearch}
                  onChange={e => setInventorySearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 280 }}
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total available VINs: <strong>{filteredVehicles.length}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total selected VINs: <strong>{preferredVinsForExpandedCell.length}</strong>
                    </Typography>
                    <Tooltip
                      arrow
                      placement="top"
                      title={hasSelections ? 'Clear all selected VINs' : 'No VINs selected'}
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={handleClearSelections}
                          disabled={!hasSelections}
                          aria-label="Clear all selected VINs"
                          sx={{ p: 0.25 }}
                        >
                          <DeselectIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  {missingVinsForExpandedCell.length > 0 && (
                    <Typography variant="body2" color="warning.main" fontWeight={600}>
                      Missing VINs: <strong>{filteredMissingVins.length}</strong>
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gridAutoRows: 'min-content',
                  alignContent: 'flex-start',
                  gap: 1.5,
                  // Only this container scrolls; outer modal stays fixed.
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                }}
              >
                {filteredVehicles.length === 0 && filteredMissingVins.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, gridColumn: '1 / -1' }}>
                    No vehicles match the search.
                  </Typography>
                ) : (
                  <>
                    {filteredVehicles.map((v) => (
                      <VehicleCard
                        key={v._id}
                        vehicle={v}
                        checked={preferredVinsForExpandedCell.includes(v.VIN)}
                        onToggle={() => handleToggleVehicle(v.VIN)}
                      />
                    ))}
                    {filteredMissingVins.map((vin) => (
                      <MissingVinCard
                        key={vin}
                        vin={vin}
                        checked={preferredVinsForExpandedCell.includes(vin)}
                        onToggle={() => handleToggleVehicle(vin)}
                      />
                    ))}
                  </>
                )}
              </Box>
            </>
          )}
          {!vehiclesLoading &&
            vehiclesForExpandedCell.length === 0 &&
            missingVinsForExpandedCell.length === 0 &&
            !vehiclesError && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, flexShrink: 0 }}>
              No vehicles for this segment and type.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
